import {
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Box,
  ModalBody,
  Flex,
  Text,
  VStack,
  InputGroup,
  InputRightElement,
  Input,
  IconButton,
  Stack,
  Skeleton,
  Button,
  Tag,
  TagLabel,
  TagCloseButton,
} from '@chakra-ui/react';

import { v4 as uuidv4 } from 'uuid';

import { useDispatch, useSelector } from 'react-redux';

import { ReducerStatus } from 'utils/constants';

import ConfirmationActionModal from 'components/common/modals/ConfirmationActionModal';
import {
  AddIcon,
  ArrowBackIcon,
  ArrowForwardIcon,
  SearchIcon,
} from '@chakra-ui/icons';
import { useEffect, useMemo, useState } from 'react';
import debounce from 'lodash.debounce';
import { Select } from 'chakra-react-select';
import FormikFormControl from 'components/common/forms/FormikFormControl';
import {
  attachSubQuestsByIds,
  getSubQuests,
} from 'store/modules/subquest/subquest-action';
import { subquestActions } from 'store/modules/subquest/subquest-slice';
import { subQuestTypes } from 'utils/constants/subQuestType';
import SubQuestToAdd from '../SubQuestToAdd';

// AddQuestSubQuestsMd - Modal component for attaching existing sub-quests to a quest
function AddQuestSubQuestsMd() {
  const dispatch = useDispatch();

  // Redux state selectors
  const addSubQuest = useSelector((state) => state.subquest.attachSubQuest);
  const newSubQuestsCount = useSelector(
    (state) => state.subquest.attachSubQuest.count
  );

  // Real data selectors
  const subQuests = useSelector((state) => state.subquest.subQuests);
  const metaData = useSelector((state) => state.subquest.metaData);
  const getSubQuestsStatus = useSelector(
    (state) => state.subquest.getSubQuestsStatus
  );
  const questSubQuests = useSelector((state) => state.subquest.questSubQuests);

  // Local state for pagination, search, and selection
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState('');
  const [searchText, setSearchText] = useState('');
  const [selectedSubQuests, setSelectedSubQuests] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);

  // Confirmation modal state
  const [pendingSubQuestsToAdd, setpendingSubQuestsToAdd] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Get all available type IDs for default filtering behavior (memoized to prevent re-creation)
  const allTypeIds = useMemo(
    () => subQuestTypes.map((option) => option.value),
    []
  );

  const getTypeIdsForAPI = useMemo(() => {
    if (selectedTypes.length === 0) {
      return allTypeIds;
    }
    // Return only selected type IDs
    return selectedTypes.map((type) => type.value);
  }, [selectedTypes, allTypeIds]);

  // Update pagination logic to use real metadata
  const hasNextPage = metaData?.hasNext || false;
  const hasPrevPage = page > 1;

  // This is used to filter out subquests that are already attached to the quest
  const attachedSubQuestIds = useMemo(() => {
    const currentQuestData = questSubQuests.find(
      (item) => item.id === addSubQuest.questId
    );
    return currentQuestData
      ? currentQuestData.subQuests.map((sq) => sq.subQuest.id)
      : [];
  }, [questSubQuests, addSubQuest.questId]);

  const filteredSubQuests = useMemo(() => {
    return subQuests.filter(
      (subQuest) => !attachedSubQuestIds.includes(subQuest.id)
    );
  }, [subQuests, attachedSubQuestIds]);

  const handleToggleModal = (isOpen) => {
    dispatch(subquestActions.setAttachSubQuest({ modal: isOpen }));
  };

  /**
   * Handles checkbox selection/deselection for sub-quests
   * Stores both ID and name for better UX in confirmation and display
   * @param {number} subQuestId - ID of the sub-quest to toggle
   * @param {string} subQuestName - Name of the sub-quest to toggle
   * @param {boolean} isChecked - Whether the checkbox is checked
   */
  const handleCheckboxChange = (subQuestId, subQuestName, isChecked) => {
    if (isChecked) {
      setSelectedSubQuests([
        ...selectedSubQuests,
        { id: subQuestId, name: subQuestName },
      ]);
    } else {
      setSelectedSubQuests(
        selectedSubQuests.filter((item) => item.id !== subQuestId)
      );
    }
  };

  const handleAttachSubQuests = () => {
    if (selectedSubQuests.length === 0) {
      return;
    }
    setpendingSubQuestsToAdd(selectedSubQuests);
    setShowConfirmation(true);
  };

  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
    setpendingSubQuestsToAdd([]);
  };

  const handleConfirmAttachment = () => {
    if (pendingSubQuestsToAdd.length === 0) return;

    // Extract IDs for dispatch to API call
    const subQuestIdsOnly = pendingSubQuestsToAdd.map((item) => item.id);

    dispatch(
      attachSubQuestsByIds({
        courseId: addSubQuest.courseId,
        questId: addSubQuest.questId,
        subQuestIds: subQuestIdsOnly,
      })
    );
  };

  const handleConfirmationResult = () => {
    dispatch(
      subquestActions.setAttachSubQuest({
        modal: false,
        id: null,
        questId: null,
        courseId: null,
        count: 0,
        status: ReducerStatus.IDLE,
        error: null,
      })
    );
    setSelectedSubQuests([]);
    setpendingSubQuestsToAdd([]);
    setShowConfirmation(false);
    handleToggleModal(false);
  };

  const getSuccessMessage = () => {
    return `Successfully attached ${newSubQuestsCount} sub-quest(s) to the selected quest.`;
  };

  const getConfirmationDescription = () => {
    if (pendingSubQuestsToAdd.length === 0) return '';

    return (
      <>
        <Text mb={3}>
          Are you sure you want to attach{' '}
          <strong>{pendingSubQuestsToAdd.length}</strong> sub-quest
          {pendingSubQuestsToAdd.length > 1 ? 's' : ''} to this quest?
        </Text>
        <VStack align="start" spacing={1}>
          <Text fontSize="sm" color="gray.600">
            Selected Sub-Quests:
          </Text>
          <Flex gap={2} wrap="wrap">
            {pendingSubQuestsToAdd.map((subQuest) => (
              <Tag
                key={subQuest.id}
                size="sm"
                borderRadius="full"
                variant="solid"
                colorScheme="blue"
              >
                <TagLabel>
                  [{subQuest.id}] {subQuest.name}
                </TagLabel>
              </Tag>
            ))}
          </Flex>
        </VStack>
      </>
    );
  };

  // Debounced search handler
  const debouncedFetchData = useMemo(
    () =>
      debounce((textValue) => {
        setQuery(textValue);
        setPage(1);
      }, 350),
    [setQuery]
  );

  const handleSearchTextChange = (e) => {
    setSearchText(e.target.value);
    debouncedFetchData(e.target.value);
  };

  const handleTypesChange = (selectedOptions) => {
    setSelectedTypes(selectedOptions || []);
    setPage(1);
  };

  useEffect(() => {
    if (addSubQuest.modal) {
      dispatch(
        getSubQuests({
          currentPage: page,
          pageSize: 10,
          query,
          typeIds: getTypeIdsForAPI,
        })
      );
    }
  }, [addSubQuest.modal, page, query, getTypeIdsForAPI, dispatch]);

  // Reset status when modal closes
  useEffect(() => {
    if (!addSubQuest.modal) {
      dispatch(subquestActions.resetGetSubQuestsStatus());
    }
  }, [addSubQuest.modal, dispatch]);

  // Reset states when modal opens
  useEffect(() => {
    if (addSubQuest.modal) {
      setPage(1);
      setQuery('');
      setSearchText('');
      setSelectedSubQuests([]);
      setSelectedTypes([]);
    }
  }, [addSubQuest.modal]);

  return (
    <>
      <ConfirmationActionModal
        isVisible={showConfirmation}
        onClose={handleCancelConfirmation}
        onConfirm={handleConfirmAttachment}
        onSubmitResult={handleConfirmationResult}
        status={addSubQuest.status}
        successMessage={getSuccessMessage()}
        error={addSubQuest.error}
      >
        {getConfirmationDescription()}
      </ConfirmationActionModal>

      <Modal
        size="full"
        isOpen={addSubQuest.modal}
        onClose={() => handleToggleModal(false)}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Attach Sub-Quests</ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <Box
              flex="1"
              border="1px"
              borderColor="gray.300"
              boxShadow="base"
              borderRadius="lg"
              p={5}
            >
              <VStack gap={3} align="stretch">
                <Text as="samp" fontSize="xs">
                  <strong>SELECT SUB-QUESTS</strong>
                </Text>

                <Flex
                  direction={{ base: 'column', lg: 'row' }}
                  alignItems="flex-end"
                  gap={2}
                >
                  <InputGroup>
                    <InputRightElement pointerEvents="none">
                      <SearchIcon color="gray.300" />
                    </InputRightElement>
                    <Input
                      name="query"
                      placeholder="Search Sub-Quests"
                      value={searchText}
                      onChange={handleSearchTextChange}
                    />
                  </InputGroup>

                  <FormikFormControl label="" helperText="">
                    <Select
                      placeholder="All Types"
                      value={selectedTypes}
                      onChange={handleTypesChange}
                      options={subQuestTypes}
                      isMulti
                      isClearable
                    />
                  </FormikFormControl>

                  <Button
                    isLoading={addSubQuest.status === ReducerStatus.LOADING}
                    onClick={handleAttachSubQuests}
                    leftIcon={<AddIcon />}
                    colorScheme="red"
                    isDisabled={selectedSubQuests.length === 0}
                    flexShrink={0}
                    width={{ base: '100%', lg: '200px' }}
                  >
                    Attach ({selectedSubQuests.length})
                  </Button>
                </Flex>

                {selectedSubQuests.length > 0 && (
                  <Box>
                    <Text mb={2}>Selected Sub-Quests:</Text>

                    <Flex gap={2} wrap="wrap">
                      {selectedSubQuests.map((subQuest) => (
                        <Tag
                          key={uuidv4()}
                          size="sm"
                          borderRadius="full"
                          variant="solid"
                          colorScheme="blue"
                        >
                          <TagLabel>
                            [{subQuest.id}] {subQuest.name}
                          </TagLabel>
                          <TagCloseButton
                            onClick={() =>
                              handleCheckboxChange(
                                subQuest.id,
                                subQuest.name,
                                false
                              )
                            }
                          />
                        </Tag>
                      ))}
                    </Flex>
                  </Box>
                )}

                {getSubQuestsStatus === ReducerStatus.LOADING ? (
                  <Stack gap={4}>
                    {[...Array(5)].map(() => (
                      <Skeleton
                        key={uuidv4()}
                        w="100%"
                        height="120px"
                        borderRadius="md"
                      />
                    ))}
                  </Stack>
                ) : (
                  filteredSubQuests?.map((subQuest) => (
                    <SubQuestToAdd
                      key={subQuest.id}
                      subQuest={subQuest}
                      handleCheckboxChange={handleCheckboxChange}
                      checkValue={
                        selectedSubQuests.find(
                          (item) => item.id === subQuest.id
                        ) != null
                      }
                    />
                  ))
                )}

                {/* Pagination controls */}
                {metaData && metaData.totalItems > 5 && (
                  <Flex mt={5} mx={1} gap={2} justify="center" align="center">
                    <IconButton
                      size="sm"
                      colorScheme="blue"
                      isDisabled={!hasPrevPage}
                      aria-label="previous"
                      onClick={() => setPage(page - 1)}
                      icon={<ArrowBackIcon />}
                    />

                    <IconButton
                      size="sm"
                      colorScheme="blue"
                      aria-label="next"
                      isDisabled={!hasNextPage}
                      onClick={() => setPage(page + 1)}
                      icon={<ArrowForwardIcon />}
                    />
                  </Flex>
                )}

                {filteredSubQuests?.length === 0 &&
                  getSubQuestsStatus === ReducerStatus.SUCCEEDED && (
                    <Flex w="100%" h="100px" justify="center" align="center">
                      <Text fontSize="lg" color="gray.500">
                        {subQuests?.length > 0
                          ? 'All available sub-quests are already attached to this quest'
                          : 'No sub-quests available'}
                      </Text>
                    </Flex>
                  )}
              </VStack>
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}

export default AddQuestSubQuestsMd;
